import { useState } from 'react';
import { useQuery, useMutation } from 'urql';
import { useWorkspace } from '@/stores/workspace.store';
import {
  Q_AUTOMATION_RULES, Q_PROJECTS,
  M_CREATE_AUTOMATION, M_PAUSE_AUTOMATION, M_RUN_AUTOMATION_NOW,
} from '@/lib/queries';
import { useUi } from '@/stores/ui.store';

const PLATFORMS = ['linkedin', 'twitter', 'facebook', 'instagram', 'tiktok', 'youtube', 'threads', 'pinterest'];
const APPROVAL_MODES = [
  { id: 'full_auto',    label: 'Full auto',     desc: 'Generate, schedule, publish — no human step' },
  { id: 'review_first', label: 'Review first',  desc: 'Generate + queue; auto-publish after approval' },
  { id: 'manual',       label: 'Manual',        desc: 'Generate only; a human schedules' },
];
const CADENCE_PRESETS = [
  { id: 'every 24h', label: 'Daily' },
  { id: 'every 12h', label: 'Twice a day' },
  { id: 'every 72h', label: '~Twice a week' },
  { id: 'every 168h', label: 'Weekly' },
];

export default function AutomationRoute() {
  const { toast } = useUi();
  const [projects] = useQuery({ query: Q_PROJECTS });
  const [rules, refetchRules] = useQuery({ query: Q_AUTOMATION_RULES });
  const [, createRule] = useMutation(M_CREATE_AUTOMATION);
  const [, pauseRule] = useMutation(M_PAUSE_AUTOMATION);
  const [, runNow] = useMutation(M_RUN_AUTOMATION_NOW);

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    name: '', platform: 'linkedin', cadence: 'every 72h',
    approvalMode: 'review_first', topics: '',
  });

  const projectId = (useWorkspace.getState().project_id ?? (projects.data?.projects?.find((p:any)=>p.id===useWorkspace.getState().project_id) ?? projects.data?.projects?.[0])?.id);
  const ruleList = rules.data?.automationRules ?? [];

  const submit = async () => {
    if (!projectId) { toast('No project found', 'error'); return; }
    if (!form.name.trim() || !form.topics.trim()) { toast('Name and at least one topic required', 'warning'); return; }
    const r = await createRule({
      input: {
        projectId,
        platform: form.platform,
        name: form.name,
        cadence: form.cadence,
        approvalMode: form.approvalMode,
        topics: form.topics.split(',').map((s) => s.trim()).filter(Boolean),
      },
    });
    if (r.error) { toast(r.error.message, 'error'); return; }
    toast(`Automation "${form.name}" created`);
    setShowForm(false);
    setForm({ name: '', platform: 'linkedin', cadence: 'every 72h', approvalMode: 'review_first', topics: '' });
    refetchRules({ requestPolicy: 'network-only' });
  };

  return (
    <div className="p-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-stone-900">Automation</h1>
          <p className="text-sm text-stone-500 mt-1">Zero-touch publishing — set a cadence and let the engine generate, schedule, and post.</p>
        </div>
        <button onClick={() => setShowForm((s) => !s)} className="px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg text-sm font-medium">
          {showForm ? 'Cancel' : '+ New automation'}
        </button>
      </div>

      {showForm && (
        <div className="bg-white border border-stone-200 rounded-xl p-6 space-y-4">
          <h2 className="font-semibold text-stone-900">New automation rule</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-xs font-medium text-stone-600">Name</span>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Weekly AI-in-education series"
                className="mt-1 w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-stone-600">Platform</span>
              <select value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })}
                className="mt-1 w-full px-3 py-2 border border-stone-300 rounded-lg text-sm bg-white">
                {PLATFORMS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-medium text-stone-600">Cadence</span>
              <select value={form.cadence} onChange={(e) => setForm({ ...form, cadence: e.target.value })}
                className="mt-1 w-full px-3 py-2 border border-stone-300 rounded-lg text-sm bg-white">
                {CADENCE_PRESETS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-medium text-stone-600">Approval mode</span>
              <select value={form.approvalMode} onChange={(e) => setForm({ ...form, approvalMode: e.target.value })}
                className="mt-1 w-full px-3 py-2 border border-stone-300 rounded-lg text-sm bg-white">
                {APPROVAL_MODES.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
              </select>
              <span className="text-[11px] text-stone-400 mt-1 block">{APPROVAL_MODES.find((m) => m.id === form.approvalMode)?.desc}</span>
            </label>
          </div>
          <label className="block">
            <span className="text-xs font-medium text-stone-600">Topics (comma-separated)</span>
            <input value={form.topics} onChange={(e) => setForm({ ...form, topics: e.target.value })}
              placeholder="career tips, study skills, industry trends"
              className="mt-1 w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" />
            <span className="text-[11px] text-stone-400 mt-1 block">The engine rotates through these for each generated post.</span>
          </label>
          <div className="flex justify-end">
            <button onClick={submit} className="px-5 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg text-sm font-medium">Create automation</button>
          </div>
        </div>
      )}

      {rules.fetching ? (
        <p className="text-stone-500">Loading rules…</p>
      ) : ruleList.length === 0 ? (
        <div className="bg-white border border-dashed border-stone-300 rounded-xl p-10 text-center">
          <p className="text-stone-600 font-medium">No automations yet.</p>
          <p className="text-sm text-stone-400 mt-1">Create one to start hands-free publishing.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {ruleList.map((r: any) => (
            <div key={r.id} className="bg-white border border-stone-200 rounded-xl p-4 flex items-center gap-4">
              <span className="text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded bg-stone-100 text-stone-700">{r.platform}</span>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-stone-900 truncate">{r.name}</p>
                <p className="text-[11px] text-stone-500">
                  {r.cadence} · {r.approvalMode} · {r.topics.length} topics · {r.runCount} runs ({r.successCount}✓ / {r.failureCount}✗)
                </p>
              </div>
              <span className={`text-[10px] uppercase font-medium px-2 py-0.5 rounded ${
                r.status === 'active' ? 'bg-emerald-50 text-emerald-700' : r.status === 'paused' ? 'bg-amber-50 text-amber-700' : 'bg-stone-100 text-stone-600'
              }`}>{r.status}</span>
              <button onClick={async () => { const x = await runNow({ id: r.id }); toast(x.error ? x.error.message : 'Run queued', x.error ? 'error' : 'success'); }}
                className="px-3 py-1.5 text-xs border border-violet-200 text-violet-700 hover:bg-violet-50 rounded font-medium">▶ Run now</button>
              {r.status === 'active' && (
                <button onClick={async () => { await pauseRule({ id: r.id }); toast('Paused'); refetchRules({ requestPolicy: 'network-only' }); }}
                  className="px-3 py-1.5 text-xs border border-stone-200 text-stone-600 hover:bg-stone-50 rounded font-medium">⏸ Pause</button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
